import { CSSObject, Interpolation, SerializedStyles } from '@emotion/react'
import {
  DropdownMenuContentProps,
  DropdownMenuProps,
} from '@radix-ui/react-dropdown-menu'
import { useState } from 'react'
import { Color, Theme } from '@opallabs/gem'
import { useTheme, ThemeProvider } from '@opallabs/hooks'
import { Button, ButtonIconPosition, ButtonProps } from '../../Button'
import { Icon } from '../../Icon'
import { Tooltip } from '../../Tooltip'
import * as DropdownMenu from '../DropdownMenu'

export type ActionsMenuProps = Pick<
  DropdownMenuContentProps,
  'align' | 'side'
> &
  Pick<DropdownMenuProps, 'onOpenChange'> &
  Pick<ButtonProps, 'variant' | 'size'> & {
    buttonStyles?: SerializedStyles | CSSObject
    contentStyles?: Interpolation<any>
    className?: string
    glyph?: string
    glyphColor?: 'Primary' | 'Secondary'
    showArrow?: boolean
    displayMenuIndicator?: boolean
    /** @deprecated React components should use ThemeProvider instead */
    themeFromBackbone?: Theme
    tooltipContent?: string
    zIndex?: number | undefined
  }

const ActionsMenuComponent: React.FC<
  React.PropsWithChildren<Omit<ActionsMenuProps, 'themeFromBackbone'>>
> = ({
  align,
  buttonStyles,
  children,
  className,
  contentStyles,
  glyph = 'gem-more-vertical',
  glyphColor = 'Primary',
  displayMenuIndicator,
  onOpenChange,
  showArrow,
  side,
  size,
  tooltipContent,
  variant = 'tertiary',
  zIndex,
}) => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)

  return (
    <DropdownMenu.Root
      open={open}
      onOpenChange={(open) => {
        setOpen(open)
        onOpenChange?.(open)
      }}
    >
      <Tooltip
        disabled={!tooltipContent}
        content={tooltipContent ?? ''}
        zIndex={zIndex}
      >
        <DropdownMenu.Trigger asChild>
          <Button
            css={buttonStyles}
            className={className}
            name="Actions"
            icon={
              <Icon
                ariaHidden
                glyph={glyph}
                useHypernova={!glyph.includes('gem')}
                color={Color[theme].Icon[glyphColor]}
              />
            }
            iconPosition={
              displayMenuIndicator ? ButtonIconPosition.leading : undefined
            }
            variant={variant}
            size={size}
            theme={theme}
            data-testid="actions-menu-options"
            ariaLabel="More options"
            displayMenuIndicator={displayMenuIndicator}
            menuIndicator={<Icon ariaHidden glyph="gem-chevron-s" />}
            menuIndicatorPosition={ButtonIconPosition.trailing}
          />
        </DropdownMenu.Trigger>
      </Tooltip>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          className={className}
          css={contentStyles}
          side={side}
          showArrow={showArrow}
          zIndex={zIndex}
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
ActionsMenuComponent.displayName =
  'Lapidary: ActionsMenuComponent ← ActionsMenuBase'

const ActionsMenuBase: React.FC<React.PropsWithChildren<ActionsMenuProps>> = ({
  themeFromBackbone,
  children,
  ...props
}) => {
  if (themeFromBackbone) {
    return (
      <ThemeProvider theme={themeFromBackbone}>
        <ActionsMenuComponent {...props}>{children}</ActionsMenuComponent>
      </ThemeProvider>
    )
  }

  return <ActionsMenuComponent {...props}>{children}</ActionsMenuComponent>
}
ActionsMenuBase.displayName = 'Lapidary: ActionsMenuBase'

export const ActionsMenu = {
  Base: ActionsMenuBase,
  Item: DropdownMenu.Item,
  Portal: DropdownMenu.Portal,
  RadioGroup: DropdownMenu.RadioGroup,
  RadioItem: DropdownMenu.RadioItem,
  Separator: DropdownMenu.Separator,
  Sub: DropdownMenu.Sub,
  SubContent: DropdownMenu.SubContent,
  SubTrigger: DropdownMenu.SubTrigger,
}
